"use client";

import type {
  ColumnPinningState,
  RowPinningState,
  SortingState,
  VisibilityState,
} from "@tanstack/react-table";

type ColumnType = "number" | "date" | "string";
type SortDirection = "asc" | "desc";
type ColumnSizingRule = {
  size?: number | null;
  minSize?: number | null;
  maxSize?: number | null;
};
type RowPinningRule = {
  top?: number[] | null;
  bottom?: number[] | null;
};

const MIN_COLUMN_SIZE_PX = 40;
const MAX_COLUMN_SIZE_PX = 1200;

/**
 * テーブル行数制限を UI 安全値へ正規化する。
 *
 * 下限のみ担保し、上限は設けない。大量行の描画責務は行数制限ではなく
 * 仮想スクロールなどの描画戦略で扱う前提とする。
 */
export function clampTableLimit(value: number | null | undefined): number {
  if (!value || !Number.isFinite(value)) {
    return 12;
  }
  return Math.max(1, Math.floor(value));
}

export function resolveInitialSortingState(args: {
  columns: string[];
  columnTypeByName: Map<string, ColumnType>;
  defaultSort?: { key: string; direction: SortDirection } | null;
}): SortingState {
  const { columns, defaultSort, columnTypeByName } = args;
  if (columns.length === 0) return [];

  if (
    defaultSort &&
    columns.includes(defaultSort.key) &&
    (defaultSort.direction === "asc" || defaultSort.direction === "desc")
  ) {
    return [{ id: defaultSort.key, desc: defaultSort.direction === "desc" }];
  }

  const numericKey = columns.find(
    (key) => columnTypeByName.get(key) === "number",
  );
  if (numericKey) return [{ id: numericKey, desc: true }];

  const dateKey = columns.find((key) => columnTypeByName.get(key) === "date");
  if (dateKey) return [{ id: dateKey, desc: false }];

  const firstKey = columns[0];
  if (!firstKey) return [];
  return [{ id: firstKey, desc: false }];
}

export function buildInitialColumnVisibility(args: {
  columns: string[];
  defaultVisibleColumns?: string[] | null;
  fieldDefaultVisibleMap: Map<string, boolean>;
}): VisibilityState {
  const { columns, defaultVisibleColumns, fieldDefaultVisibleMap } = args;

  const configuredVisibleSet =
    Array.isArray(defaultVisibleColumns) && defaultVisibleColumns.length > 0
      ? new Set(defaultVisibleColumns)
      : null;

  const visibility: VisibilityState = {};
  for (const key of columns) {
    const fromConfig = configuredVisibleSet?.has(key);
    const fromField = fieldDefaultVisibleMap.get(key);
    const isVisible =
      configuredVisibleSet !== null ? fromConfig === true : fromField !== false;

    if (!isVisible) visibility[key] = false;
  }

  return visibility;
}

export function normalizeColumnPinningState(args: {
  columns: string[];
  columnPinning?: {
    left?: string[] | null;
    right?: string[] | null;
  } | null;
}): ColumnPinningState {
  const { columns, columnPinning } = args;
  const available = new Set(columns);

  const left: string[] = [];
  for (const key of columnPinning?.left ?? []) {
    if (!available.has(key)) continue;
    if (left.includes(key)) continue;
    left.push(key);
  }

  const right: string[] = [];
  const leftSet = new Set(left);
  for (const key of columnPinning?.right ?? []) {
    if (!available.has(key)) continue;
    if (leftSet.has(key)) continue;
    if (right.includes(key)) continue;
    right.push(key);
  }

  return { left, right };
}

function normalizeColumnSize(value: number | null | undefined): number | null {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return Math.max(
    MIN_COLUMN_SIZE_PX,
    Math.min(MAX_COLUMN_SIZE_PX, Math.round(value)),
  );
}

export function normalizeColumnSizingConfig(args: {
  columns: string[];
  columnSizing?: Record<string, ColumnSizingRule | null | undefined> | null;
}): Record<string, { size?: number; minSize?: number; maxSize?: number }> {
  const { columns, columnSizing } = args;
  if (!columnSizing) return {};

  const available = new Set(columns);
  const resolved: Record<
    string,
    { size?: number; minSize?: number; maxSize?: number }
  > = {};

  for (const [key, sizing] of Object.entries(columnSizing)) {
    if (!available.has(key)) continue;
    if (!sizing) continue;

    const minSize = normalizeColumnSize(sizing.minSize);
    const maxSize = normalizeColumnSize(sizing.maxSize);
    const floor = minSize ?? MIN_COLUMN_SIZE_PX;
    const ceil = maxSize ?? MAX_COLUMN_SIZE_PX;
    const normalizedMin = Math.min(floor, ceil);
    const normalizedMax = Math.max(floor, ceil);
    const size = normalizeColumnSize(sizing.size);

    const next: { size?: number; minSize?: number; maxSize?: number } = {};
    if (minSize !== null || maxSize !== null) {
      next.minSize = normalizedMin;
      next.maxSize = normalizedMax;
    }
    if (size !== null) {
      next.size = Math.max(normalizedMin, Math.min(normalizedMax, size));
    }

    if (Object.keys(next).length === 0) continue;
    resolved[key] = next;
  }

  return resolved;
}

export function normalizeRowPinningState(args: {
  rowCount: number;
  rowPinning?: RowPinningRule | null;
}): RowPinningState {
  const maxIndex = Math.max(0, args.rowCount - 1);
  if (args.rowCount <= 0) {
    return { top: [], bottom: [] };
  }

  const top: string[] = [];
  for (const rawIndex of args.rowPinning?.top ?? []) {
    if (!Number.isInteger(rawIndex)) continue;
    if (rawIndex < 0 || rawIndex > maxIndex) continue;
    const id = String(rawIndex);
    if (top.includes(id)) continue;
    top.push(id);
  }

  const bottom: string[] = [];
  const topSet = new Set(top);
  for (const rawIndex of args.rowPinning?.bottom ?? []) {
    if (!Number.isInteger(rawIndex)) continue;
    if (rawIndex < 0 || rawIndex > maxIndex) continue;
    const id = String(rawIndex);
    if (topSet.has(id)) continue;
    if (bottom.includes(id)) continue;
    bottom.push(id);
  }

  return { top, bottom };
}

export function areSortingStatesEqual(
  left: SortingState,
  right: SortingState,
): boolean {
  if (left.length !== right.length) return false;
  return left.every(
    (item, index) =>
      item.id === right[index]?.id && item.desc === right[index]?.desc,
  );
}

export function areVisibilityStatesEqual(
  left: VisibilityState,
  right: VisibilityState,
): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
}

export function areColumnPinningStatesEqual(
  left: ColumnPinningState,
  right: ColumnPinningState,
): boolean {
  const leftLeft = left.left ?? [];
  const rightLeft = right.left ?? [];
  const leftRight = left.right ?? [];
  const rightRight = right.right ?? [];

  if (leftLeft.length !== rightLeft.length) return false;
  if (leftRight.length !== rightRight.length) return false;

  return (
    leftLeft.every((key, index) => key === rightLeft[index]) &&
    leftRight.every((key, index) => key === rightRight[index])
  );
}

export function areRowPinningStatesEqual(
  left: RowPinningState,
  right: RowPinningState,
): boolean {
  const leftTop = left.top ?? [];
  const rightTop = right.top ?? [];
  const leftBottom = left.bottom ?? [];
  const rightBottom = right.bottom ?? [];

  if (leftTop.length !== rightTop.length) return false;
  if (leftBottom.length !== rightBottom.length) return false;

  return (
    leftTop.every((key, index) => key === rightTop[index]) &&
    leftBottom.every((key, index) => key === rightBottom[index])
  );
}
